import { z } from 'zod';
import { UNIT_KINDS, UNIT_STATUSES } from './read-api.js';
import { MAX_CELL_LENGTH, UNIT_CELL_FIELDS } from './unit-cells.js';
// Contracts of the master-data endpoints (Phase 6.6A). Request schemas are shared by the server
// (authoritative validation) and the future client (early feedback). All objects are strict.
const trimmed = (min, max) => z
    .string()
    .refine((value) => value === value.trim(), 'untrimmed')
    .pipe(z.string().min(min).max(max));
/** Editable unit fields and their rules (mirrors the database checks, docs/12 §4.1). */
const unitFields = {
    code: trimmed(1, 32),
    displayNumber: trimmed(1, 12),
    kind: z.enum(UNIT_KINDS),
    structureId: z.uuid(),
    levelId: z.uuid().nullable(),
    modelType: trimmed(1, 40).nullable(),
    rooms: z.number().gt(0).max(20).multipleOf(0.5).nullable(),
    areaM2: z.number().gt(0).max(99_999.99).multipleOf(0.01).nullable(),
    status: z.enum(UNIT_STATUSES),
};
const modelTypeOnlyForApartments = (unit) => unit.modelType === undefined ||
    unit.modelType === null ||
    unit.kind === undefined ||
    unit.kind === 'apartment';
/** `POST /projects/:projectId/units`. */
export const unitCreateRequestSchema = z
    .object({
    code: unitFields.code,
    displayNumber: unitFields.displayNumber,
    kind: unitFields.kind,
    structureId: unitFields.structureId,
    levelId: unitFields.levelId.default(null),
    modelType: unitFields.modelType.default(null),
    rooms: unitFields.rooms.default(null),
    areaM2: unitFields.areaM2.default(null),
    status: unitFields.status.default('planned'),
})
    .strict()
    .refine(modelTypeOnlyForApartments, { path: ['modelType'], message: 'apartments_only' });
/** `PATCH /projects/:projectId/units/:unitId`: the version the client edited, and the changed fields only. */
export const unitUpdateRequestSchema = z
    .object({
    rowVersion: z.number().int().min(1),
    changes: z
        .object(unitFields)
        .partial()
        .strict()
        .refine((changes) => Object.keys(changes).length > 0, 'no_changes'),
})
    .strict();
/** `GET /projects/:projectId/structures`: structures (buildings) with their levels (floors). */
export const structureListResponseSchema = z
    .object({
    items: z.array(z
        .object({
        id: z.uuid(),
        code: z.string(),
        name: z.string(),
        kind: z.enum(['residential', 'parking', 'storage', 'commercial', 'other']),
        levels: z.array(z.object({ id: z.uuid(), number: z.number().int(), label: z.string() }).strict()),
    })
        .strict()),
})
    .strict();
const auditValue = z.union([z.string(), z.number(), z.null()]);
/** `GET /projects/:projectId/units/:unitId/history`: newest first. */
export const unitHistoryResponseSchema = z
    .object({
    items: z.array(z
        .object({
        id: z.uuid(),
        // Changes of contacts or the note name the changed fields only (DR-185, DR-187).
        action: z.enum([
            'projects.unit.created',
            'projects.unit.updated',
            'projects.apartment_personal.updated',
        ]),
        occurredAt: z.iso.datetime(),
        actor: z.object({ id: z.uuid(), displayName: z.string() }).strict().nullable(),
        before: z.record(z.string(), auditValue).nullable(),
        after: z.record(z.string(), auditValue).nullable(),
    })
        .strict()),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// Batch preview and save (DR-147, Phase 6.6B). Rows carry raw grid cells; the server parses them
// with the shared `parseUnitCells`, resolves buildings and floors within the project, and
// re-validates everything at commit time. A batch never deletes anything.
export const UNIT_BATCH_MAX_ROWS = 500;
const cellsSchema = z
    .object(Object.fromEntries(UNIT_CELL_FIELDS.map((field) => [field, z.string().max(MAX_CELL_LENGTH).optional()])))
    .strict();
const clientRowId = z.string().regex(/^[A-Za-z0-9_-]{1,64}$/);
export const unitBatchRowSchema = z.discriminatedUnion('op', [
    z.object({ op: z.literal('create'), clientRowId, cells: cellsSchema }).strict(),
    z
        .object({
        op: z.literal('update'),
        clientRowId,
        unitId: z.uuid(),
        /** The version the user's change is based on. */
        rowVersion: z.number().int().min(1),
        cells: cellsSchema,
    })
        .strict(),
]);
/** `POST …/units/batch/preview` and `POST …/units/batch`. `batchId` makes a commit safely retryable. */
export const unitBatchRequestSchema = z
    .object({
    batchId: z.uuid(),
    rows: z.array(unitBatchRowSchema).min(1).max(UNIT_BATCH_MAX_ROWS),
})
    .strict()
    .superRefine((batch, ctx) => {
    const seenRows = new Set();
    const seenUnits = new Set();
    batch.rows.forEach((row, index) => {
        if (seenRows.has(row.clientRowId)) {
            ctx.addIssue({
                code: 'custom',
                path: ['rows', index, 'clientRowId'],
                message: 'duplicate_row',
            });
        }
        seenRows.add(row.clientRowId);
        if (row.op === 'update') {
            if (seenUnits.has(row.unitId)) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['rows', index, 'unitId'],
                    message: 'duplicate_row',
                });
            }
            seenUnits.add(row.unitId);
        }
    });
});
export const UNIT_BATCH_ROW_STATUSES = [
    'create',
    'update',
    'unchanged',
    'invalid',
    'duplicate',
    'conflict',
];
export const unitBatchResponseSchema = z
    .object({
    batchId: z.uuid(),
    /** True only when the whole batch was written. */
    committed: z.boolean(),
    /** True when a retried commit returned the result stored by the first attempt. */
    replayed: z.boolean(),
    summary: z
        .object(Object.fromEntries(UNIT_BATCH_ROW_STATUSES.map((s) => [s, z.number().int().min(0)])))
        .strict(),
    rows: z.array(z
        .object({
        clientRowId,
        status: z.enum(UNIT_BATCH_ROW_STATUSES),
        /** Set for updates and conflicts, and for creations once committed. */
        unitId: z.uuid().nullable(),
        /** After commit: the new version. For conflicts: the current stored version. */
        rowVersion: z.number().int().min(1).nullable(),
        /** Field → error code (see CELL_ERROR_MESSAGES). */
        fields: z.record(z.string(), z.string()),
    })
        .strict()),
})
    .strict();
//# sourceMappingURL=master-data.js.map